import { type SubmitEvent as ReactSubmitEvent, type FormEvent, memo, useState } from "react";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import "Components/ConfigLayout/Network/SecuritySettings/SecuritySettings.less";
import { Card, type SelectComponent, SettingsLayout, Tabs } from "@clintonelec/react-storybook";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import {
	selectNetworkSecuritySettings, setNetworkSecuritySettingsAction
} from "Data/Redux/Slices/Settings/Network/Security";
import type {
	EapType, EncryptionType, HttpAuthenticationType, IpFilter, NetworkSecurityState, IpFilterRule,
	SecurityCertificate, RtspAuthenticationType
} from "Interfaces";
import { produce } from "immer";
import { cloneDeep, isEqual, merge } from "lodash";
import IeeeTab from "Components/ConfigLayout/Network/SecuritySettings/IeeeTab";
import IpFilterTab from "Components/ConfigLayout/Network/SecuritySettings/IpFilterTab";
import EncryptionTab from "Components/ConfigLayout/Network/SecuritySettings/EncryptionTab";

type SecuritySettingsForm = {
	httpAuthType: SelectComponent;
	httpEnabled: SelectComponent;
	ipFilterEnabled: SelectComponent;
	ipFilterRule: SelectComponent;
	authSelect: SelectComponent;
	ieeeEnabled: SelectComponent;
	eapType: SelectComponent;
	eapolVersion: SelectComponent;
	ieeeId: HTMLInputElement;
	ieeePassword: HTMLInputElement;
} & HTMLFormElement;

const emptySecuritySettingsState: NetworkSecurityState = {
	certificates: [],
	ieeeOptions: {},
	ipFiltering: {},
	securityOptions: {},
	sslCertificates: []
};

function SecuritySettings() {
	const dispatch = useAppDispatch();
	const securitySettings = useAppSelector(selectNetworkSecuritySettings);
	const [ securitySettingsDiff, setSecuritySettingsDiff ] = useState<NetworkSecurityState>(
		emptySecuritySettingsState
	);

	const setNetworkSecuritySettings = (newOptions: NetworkSecurityState) => dispatch(
		setNetworkSecuritySettingsAction(newOptions)
	);

	const localSecuritySettings = produce(securitySettings, draft => {
		merge(draft, securitySettingsDiff);
		draft.sslCertificates = securitySettingsDiff.sslCertificates?.length !== 0
			? securitySettingsDiff.sslCertificates as SecurityCertificate[]
			: securitySettings.sslCertificates;

		draft.certificates = securitySettingsDiff.certificates?.length !== 0
			? securitySettingsDiff.certificates as SecurityCertificate[]
			: securitySettings.certificates;

		draft.ipFiltering.filters = securitySettingsDiff.ipFiltering?.filters ?? securitySettings.ipFiltering.filters;
	});

	const handleFormSubmit = (event: ReactSubmitEvent<SecuritySettingsForm>) => {
		event.preventDefault();

		setNetworkSecuritySettings(localSecuritySettings);
		setSecuritySettingsDiff(emptySecuritySettingsState);
	};

	const handleReset = (event: FormEvent<SecuritySettingsForm>) => {
		event.preventDefault();

		setSecuritySettingsDiff(emptySecuritySettingsState);
	};

	const setFilterEnabled = (checked: boolean) => {
		const newState = produce(securitySettingsDiff, (draft) => {
			draft.ipFiltering.ipFilterEnabled = checked;
			draft.ipFiltering.ipFilterRule = securitySettings.ipFiltering.ipFilterRule;
		});

		setSecuritySettingsDiff(newState);
	};

	const setFilterAction = (filter: IpFilter | string, oldFilterAddress?: string) => {
		const { ipFiltering: { filters } } = securitySettings;
		const newState = produce(securitySettingsDiff, (draft) => {
			if (!draft.ipFiltering?.filters) {
				draft.ipFiltering.filters = cloneDeep(filters);
			}

			const { ipFiltering: { filters: draftFilters } } = draft;

			if (typeof filter === "string") {
				draft.ipFiltering.filters = draftFilters?.filter(current => current.addresses[ 0 ] !== filter);
			} else {
				const foundIndex = draftFilters?.findIndex(current => current.addresses[ 0 ] === oldFilterAddress);

				if (foundIndex && foundIndex < 0) {
					draft.ipFiltering.filters?.push(filter);
				} else {
					draft.ipFiltering.filters = draftFilters?.map((current, index) => {
						return index === foundIndex ? filter : current;
					});
				}
			}
		});

		setSecuritySettingsDiff(newState);
	};

	const setRtspAuth = (value: string | null) => {
		if (!value) {
			return;
		}

		const newStateDiff = produce(securitySettingsDiff, draft => {
			draft.securityOptions.rtspAuthType = value as RtspAuthenticationType;
		});

		setSecuritySettingsDiff(newStateDiff);
	};

	const setEncryptionType = (newEncryptionType: string | null) => {
		if (!newEncryptionType) {
			return;
		}

		const newStateDiff = produce(securitySettingsDiff, draft => {
			draft.securityOptions.encryptionType = newEncryptionType as EncryptionType;
		});

		setSecuritySettingsDiff(newStateDiff);
	};

	const setHttpAuthType = (value: string | null) => {
		if (!value) {
			return;
		}

		const newStateDiff = produce(securitySettingsDiff, draft => {
			draft.securityOptions.httpAuthType = value as HttpAuthenticationType;
		});

		setSecuritySettingsDiff(newStateDiff);
	};

	const setSslCertificate = (certificate: SecurityCertificate) => {
		const newState = produce(securitySettingsDiff, (draft) => {
			if (draft.sslCertificates?.length === 0) {
				draft.sslCertificates = cloneDeep(localSecuritySettings.sslCertificates);
			}

			draft.sslCertificates = draft.sslCertificates?.map(currentCert => {
				return currentCert.type === certificate.type ? certificate : currentCert;
			});
		});

		setSecuritySettingsDiff(newState);
	};

	const setEapolVersion = (value: string | null) => {
		if (!value) {
			return;
		}

		const newStateDiff = produce(securitySettingsDiff, draft => {
			draft.ieeeOptions.eapolVersion = +value;
		});

		setSecuritySettingsDiff(newStateDiff);
	};

	const setEapType = (value: string | null) => {
		if (!value) {
			return;
		}

		const newStateDiff = produce(securitySettingsDiff, draft => {
			draft.ieeeOptions.eapType = value as EapType;
		});

		setSecuritySettingsDiff(newStateDiff);
	};

	const setIpRule = (value: string | null) => {
		if (!value) {
			return;
		}

		const newStateDiff = produce(securitySettingsDiff, draft => {
			draft.ipFiltering.ipFilterRule = value as IpFilterRule;
		});

		setSecuritySettingsDiff(newStateDiff);
	};

	const setIeeeEnabled = (checked: boolean) => {
		const newStateDiff = produce(securitySettingsDiff, draft => {
			draft.ieeeOptions.enabled = checked;
		});

		setSecuritySettingsDiff(newStateDiff);
	};

	const setCertificate = (certificate: SecurityCertificate) => {
		const newState = produce(securitySettingsDiff, (draft) => {
			if (draft.certificates?.length === 0) {
				draft.certificates = cloneDeep(localSecuritySettings.certificates);
			}

			draft.certificates = draft.certificates?.map(currentCert => {
				return currentCert.type === certificate.type ? certificate : currentCert;
			});
		});

		setSecuritySettingsDiff(newState);
	};

	const setIeeeId = (value: string) => {
		const newStateDiff = produce(securitySettingsDiff, draft => {
			draft.ieeeOptions.id = value;
		});

		setSecuritySettingsDiff(newStateDiff);
	};

	const setIeeePassword = (value: string) => {
		const newStateDiff = produce(securitySettingsDiff, draft => {
			draft.ieeeOptions.password = value;
		});

		setSecuritySettingsDiff(newStateDiff);
	};

	const setIeeeClientKeyPassword = (value: string) => {
		const newStateDiff = produce(securitySettingsDiff, draft => {
			draft.ieeeOptions.clientKeyPassword = value;
		});

		setSecuritySettingsDiff(newStateDiff);
	};

	const panes = [
		{
			title: "Encryption",
			content: <EncryptionTab
				securitySettings={ localSecuritySettings }
				handleRtspAuthSelect={ setRtspAuth }
				handleEncryptionTypeSelect={ setEncryptionType }
				handleHttpAuthTypeSelect={ setHttpAuthType }
				handleSslCertificateUpdate={ setSslCertificate }
			/>
		},
		{
			title: "IP Filter",
			content: <IpFilterTab
				securitySettings={ localSecuritySettings }
				handleFilterEnabledChange={ setFilterEnabled }
				handleIpRuleSelect={ setIpRule }
				handleFilterAction={ setFilterAction }
			/>
		},
		{
			title: "802.1x",
			content: <IeeeTab
				securitySettings={ localSecuritySettings }
				handleIeeeEnabledChange={ setIeeeEnabled }
				handleEapTypeSelect={ setEapType }
				handleEapolVersionSelect={ setEapolVersion }
				handleIeeeIdUpdate={ setIeeeId }
				handleIeeePasswordUpdate={ setIeeePassword }
				handleCertificateUpdate={ setCertificate }
				handleIeeeClientKeyPasswordUpdate={ setIeeeClientKeyPassword }
			/>
		}
	];

	return (
		<SettingsLayout.Form
			className="network-security-settings"
			noValidate
			onReset={ handleReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="medium" title="Security">
					<Tabs panes={ panes } />
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ isEqual(securitySettings, localSecuritySettings) } />
		</SettingsLayout.Form>
	);
}

export default memo(SecuritySettings);
